import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import {
  catchError,
  mergeMap,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { ReviewModel } from '../../models/Review.model';
import {
  IMessagesService,
  MESSAGES_SERVICE,
} from '../../services/IMessages.service';
import * as fromReviewsActions from '../actions/reviews.actions';
import { ReviewsStore } from '../store/reviews.store';
import * as fromActions from '../messages.actions';

@Injectable()
export class ReviewsEffects {
  getReviews$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReviewsActions.getReviewsBeginAction),
      switchMap((action) =>
        this.service.getReviews().pipe(
          mergeMap((payload) =>
            of([
              fromReviewsActions.getReviewsSuccessAction({ payload }),
              fromActions.clearConversationSuccessErrorDataAction(),
            ]).pipe(switchMap((d) => d))
          ),
          catchError((error) => {
            console.error('Could not get reviews', error);
            return of([
              fromReviewsActions.getReviewsFailAction({ errors: error }),
              fromActions.clearConversationSuccessErrorDataAction(),
            ]).pipe(switchMap((d) => d));
          })
        )
      )
    )
  );

  upsertReviewReply$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReviewsActions.upsertReviewReplyBeginAction),
      withLatestFrom(this.reviewsStore.data$),
      switchMap(([action, lastData]) =>
        this.service.upsertReply(action.review, action.comment).pipe(
          mergeMap((updatedReview) => {
            const idx = lastData.findIndex(
              (review) => review.id === updatedReview.id
            );
            const payload = [...lastData];
            payload.splice(idx, 1, updatedReview);

            return of([
              fromReviewsActions.upsertReviewReplySuccessAction({
                payload,
              }),
              fromActions.clearConversationSuccessErrorDataAction(),
            ]).pipe(switchMap((d) => d));
          }),
          catchError((error) => {
            console.error('Could not upsert review reply', error);
            return of([
              fromReviewsActions.upsertReviewReplyFaliAction({ errors: error }),
              fromActions.clearConversationSuccessErrorDataAction(),
            ]).pipe(switchMap((d) => d));
          })
        )
      )
    )
  );

  deleteReviewReply$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromReviewsActions.deleteReviewReplyBeginAction),
      withLatestFrom(this.reviewsStore.data$),
      switchMap(([action, lastData]) =>
        this.service.deleteReply(action.reviewId.toString()).pipe(
          mergeMap((_) => {
            const oldReview = lastData.find((el) => el.id === action.reviewId);
            const updatedReview = new ReviewModel({
              id: oldReview.id,
              senderName: oldReview.senderName,
              message: oldReview.message,
              createTime: oldReview.createTime,
              source: oldReview.source,

              senderPhotoUrl: oldReview.senderPhotoUrl,
              starRating: oldReview.starRating,
              updateTime: oldReview.updateTime,
            });

            const idx = lastData.findIndex(
              (review) => review.id === updatedReview.id
            );
            const payload = [...lastData];
            payload.splice(idx, 1, updatedReview);

            return of([
              fromReviewsActions.deleteReviewReplySuccessAction({
                payload,
              }),
              fromActions.clearConversationSuccessErrorDataAction(),
            ]).pipe(switchMap((d) => d));
          }),
          catchError((error) => {
            console.error('Could not delete review reply', error);
            return of([
              fromReviewsActions.deleteReviewReplyFaliAction({ errors: error }),
              fromActions.clearConversationSuccessErrorDataAction(),
            ]).pipe(switchMap((d) => d));
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    @Inject(MESSAGES_SERVICE) private service: IMessagesService,
    private reviewsStore: ReviewsStore
  ) {}
}
